import { supabase, getCurrentUserId } from './supabase';

export type CariClinic = {
  id: string;
  clinic_name: string;
  contact_name: string;
  phone: string;
  created_at: string;
};

export type CariTransaction = {
  id: string;
  clinic_name: string;
  type: 'borc' | 'odeme';
  amount: number;
  date: string;
  description: string;
  surgery_id?: string;
  created_at: string;
};

export type CariSummary = {
  clinic_name: string;
  total_borc: number;
  total_odeme: number;
  bakiye: number;
  last_transaction: string;
  contact_name?: string;
  phone?: string;
};

export const cariDB = {
  /* ── Transactions ── */
  getAll: async (): Promise<CariTransaction[]> => {
    const { data, error } = await supabase
      .from('cari_transactions')
      .select('*')
      .order('date', { ascending: false });
    if (error) throw error;
    return (data ?? []) as CariTransaction[];
  },

  getByClinic: async (clinicName: string): Promise<CariTransaction[]> => {
    const { data, error } = await supabase
      .from('cari_transactions')
      .select('*')
      .eq('clinic_name', clinicName)
      .order('date', { ascending: false });
    if (error) throw error;
    return (data ?? []) as CariTransaction[];
  },

  getSurgeryTransactions: async (surgeryId: string): Promise<CariTransaction[]> => {
    const { data, error } = await supabase
      .from('cari_transactions')
      .select('*')
      .eq('surgery_id', surgeryId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as CariTransaction[];
  },

  insert: async (tx: Omit<CariTransaction, 'id' | 'created_at'>): Promise<CariTransaction> => {
    const userId = await getCurrentUserId();
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('cari_transactions')
      .insert({ ...tx, user_id: userId, created_at: now })
      .select()
      .single();
    if (error) throw error;
    return data as CariTransaction;
  },

  upsertBySurgery: async (
    surgeryId: string,
    txData: Omit<CariTransaction, 'id' | 'created_at'>
  ): Promise<void> => {
    const userId = await getCurrentUserId();
    const existing = (await cariDB.getSurgeryTransactions(surgeryId))
      .filter((tx) => tx.type === 'borc');
    const now = new Date().toISOString();
    if (existing.length > 0) {
      await Promise.all(existing.map((e) => cariDB.delete(e.id)));
    }
    const { error } = await supabase
      .from('cari_transactions')
      .insert({ ...txData, user_id: userId, created_at: now });
    if (error) throw error;
  },

  delete: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('cari_transactions')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },

  getSummaries: async (): Promise<CariSummary[]> => {
    const [allTx, allClinics] = await Promise.all([
      cariDB.getAll(),
      cariDB.getClinics(),
    ]);

    const map = new Map<string, { borc: number; odeme: number; lastDate: string }>();

    allClinics.forEach((c) => {
      if (!map.has(c.clinic_name)) map.set(c.clinic_name, { borc: 0, odeme: 0, lastDate: '' });
    });

    allTx.forEach((tx) => {
      const existing = map.get(tx.clinic_name) ?? { borc: 0, odeme: 0, lastDate: '' };
      if (tx.type === 'borc') existing.borc += tx.amount;
      else existing.odeme += tx.amount;
      if (!existing.lastDate || tx.date > existing.lastDate) existing.lastDate = tx.date;
      map.set(tx.clinic_name, existing);
    });

    const summaries: CariSummary[] = [];
    map.forEach((val, clinic_name) => {
      const info = allClinics.find((c) => c.clinic_name === clinic_name);
      summaries.push({
        clinic_name,
        total_borc: val.borc,
        total_odeme: val.odeme,
        bakiye: val.borc - val.odeme,
        last_transaction: val.lastDate,
        contact_name: info?.contact_name,
        phone: info?.phone,
      });
    });

    return summaries.sort((a, b) => b.bakiye - a.bakiye);
  },

  /* ── Clinics ── */
  getClinics: async (): Promise<CariClinic[]> => {
    const { data, error } = await supabase
      .from('cari_clinics')
      .select('*')
      .order('clinic_name');
    if (error) throw error;
    return (data ?? []) as CariClinic[];
  },

  getAllClinicsForSettings: async (): Promise<{ clinic_name: string; contact_name?: string; phone?: string; id?: string }[]> => {
    const [allClinics, allTx] = await Promise.all([
      cariDB.getClinics(),
      cariDB.getAll(),
    ]);

    const map = new Map<string, { clinic_name: string; contact_name?: string; phone?: string; id?: string }>();

    allClinics.forEach((c) => {
      map.set(c.clinic_name.trim(), {
        clinic_name: c.clinic_name,
        contact_name: c.contact_name,
        phone: c.phone,
        id: c.id,
      });
    });

    allTx.forEach((tx) => {
      const name = tx.clinic_name?.trim();
      if (name && !map.has(name)) {
        map.set(name, { clinic_name: name });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      a.clinic_name.localeCompare(b.clinic_name, 'tr')
    );
  },

  getClinicByName: async (name: string): Promise<CariClinic | undefined> => {
    const { data, error } = await supabase
      .from('cari_clinics')
      .select('*')
      .eq('clinic_name', name)
      .maybeSingle();
    if (error) return undefined;
    return data as CariClinic | undefined;
  },

  insertClinic: async (data: Omit<CariClinic, 'id' | 'created_at'>): Promise<CariClinic> => {
    const userId = await getCurrentUserId();
    const now = new Date().toISOString();
    const { data: result, error } = await supabase
      .from('cari_clinics')
      .insert({ ...data, user_id: userId, created_at: now })
      .select()
      .single();
    if (error) throw error;
    return result as CariClinic;
  },

  updateClinic: async (id: string, data: Partial<Omit<CariClinic, 'id' | 'created_at'>>): Promise<void> => {
    const { error } = await supabase
      .from('cari_clinics')
      .update(data)
      .eq('id', id);
    if (error) throw error;
  },
};
